/**
 * Handle Visibility Context (external-store based)
 *
 * Tracks which nodes should mount their ReactFlow connection handles. Backed by
 * a module-level external store + `useSyncExternalStore` so each node subscribes
 * via a SELECTOR keyed on its own id — it only re-renders when ITS OWN handle
 * visibility flips, not on every hover/selection/status change.
 *
 * Why this matters (mirrors `NodeHoverContext`): if the visible-node set lived in
 * a plain React Context value, every handle-rendering node would re-render on
 * every membership change (hover, selection, status). With per-id selector
 * subscriptions, `useSyncExternalStore`'s built-in equality check skips the
 * re-render for nodes whose projected boolean is unchanged.
 *
 * The publisher (`useSetHandleVisibility`) returns a stable setter — calling it
 * never re-renders the caller. It diffs against the current snapshot and only
 * notifies listeners when the visibility actually changes.
 */

import { useMemo, useSyncExternalStore } from 'react'
import { useNodePresentation } from './NodePresentationContext'
import {
  ALL_HANDLES_VISIBLE,
  handleVisibilityEqual,
  isNodeHandleVisible,
  type HandleVisibility,
} from './canvasHandleVisibility'

// ---------------------------------------------------------------------------
// External store
// ---------------------------------------------------------------------------

let _visibility: HandleVisibility = ALL_HANDLES_VISIBLE
const _listeners = new Set<() => void>()

function _subscribe(cb: () => void): () => void {
  _listeners.add(cb)
  return () => _listeners.delete(cb)
}

function _setHandleVisibility(next: HandleVisibility): void {
  if (handleVisibilityEqual(_visibility, next)) return
  _visibility = next
  for (const cb of _listeners) cb()
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

/**
 * Subscribe to whether a specific node's handles should be mounted. Only
 * re-renders when this node's projected boolean changes.
 */
export function useHandlesVisibleForNode(nodeId: string): boolean {
  const presentation = useNodePresentation()
  // The snapshot returned to useSyncExternalStore is the projected boolean, so
  // its Object.is check skips re-renders for nodes whose answer didn't change.
  const getSnapshot = useMemo(
    () => () => isNodeHandleVisible(_visibility, nodeId),
    [nodeId],
  )
  const visible = useSyncExternalStore(_subscribe, getSnapshot, getSnapshot)
  return (presentation === 'canvas' || presentation === 'background') && visible
}

/** Stable publisher — never causes re-renders by itself. */
export function useSetHandleVisibility(): (next: HandleVisibility) => void {
  return _setHandleVisibility
}

/** Test-only reset so module state does not leak across tests. */
export function __resetHandleVisibilityForTests(): void {
  _visibility = ALL_HANDLES_VISIBLE
  _listeners.clear()
}
